import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, FileText, Loader2, Plus, ScanLine, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  createMatterDeadline,
  deleteMatterDeadline,
  extractMatterDeadlines,
  getMatter,
  listMatterDeadlines,
  updateMatterDeadline,
  type Deadline,
  type DeadlineStatus,
} from '@/lib/api-client';
import { formatDueDate, relativeDue, urgencyClasses, urgencyOf } from '@/lib/deadlines';
import type { MatterDetail } from '@/lib/types';

// Court-order deadline tracker (Sprint P5 Section 4). Per-matter view: extract
// from court-order documents, add by hand, sort by due date, visual urgency.
export function Deadlines() {
  const { id = '' } = useParams<{ id: string }>();
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getMatter(id).catch(() => null), listMatterDeadlines(id).catch(() => [])]).then(
      ([m, d]) => {
        if (!active) return;
        setMatter(m);
        setDeadlines(d);
        setLoading(false);
      },
    );
    return () => {
      active = false;
    };
  }, [id]);

  async function scan(): Promise<void> {
    setError(null);
    setNotice(null);
    setScanning(true);
    try {
      const result = await extractMatterDeadlines(id);
      setDeadlines(result.deadlines);
      setNotice(
        result.created > 0
          ? `Found ${result.created} new deadline${result.created === 1 ? '' : 's'} in ${result.scanned} court order${result.scanned === 1 ? '' : 's'}.`
          : `Scanned ${result.scanned} court order${result.scanned === 1 ? '' : 's'}; no new deadlines.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not scan documents.');
    } finally {
      setScanning(false);
    }
  }

  async function setStatus(d: Deadline, status: DeadlineStatus): Promise<void> {
    setDeadlines(await updateMatterDeadline(id, d.id, { status }));
  }

  async function remove(d: Deadline): Promise<void> {
    await deleteMatterDeadline(id, d.id);
    setDeadlines((prev) => prev.filter((x) => x.id !== d.id));
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 font-display text-3xl italic text-stone-100">
              <CalendarClock className="h-6 w-6 text-amber-500" /> Deadlines
            </h1>
            {matter ? <p className="text-sm text-stone-400">{matter.name}</p> : null}
          </div>
          <Link
            to="/matters"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Matters
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void scan()}
            disabled={scanning}
            className="inline-flex items-center gap-2 rounded-md border border-stone-700 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-700/60 disabled:opacity-40"
          >
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
            Scan court orders for deadlines
          </button>
        </div>
        {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}
        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        <AddDeadline matterId={id} onAdded={(d) => setDeadlines((prev) => sortByDue([...prev, d]))} />

        {loading ? (
          <Card variant="default" padding="lg" className="text-sm text-stone-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </Card>
        ) : deadlines.length === 0 ? (
          <Card variant="default" padding="lg" className="text-sm text-stone-500">
            No deadlines yet. Scan your court orders, or add one by hand above.
          </Card>
        ) : (
          <div className="space-y-2">
            {deadlines.map((d) => (
              <DeadlineRow
                key={d.id}
                deadline={d}
                onStatus={(s) => void setStatus(d, s)}
                onDelete={() => void remove(d)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function sortByDue(list: Deadline[]): Deadline[] {
  return [...list].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
}

function AddDeadline({ matterId, onAdded }: { matterId: string; onAdded: (d: Deadline) => void }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(): Promise<void> {
    setError(null);
    if (!description.trim() || !dueDate) {
      setError('Enter a description and a due date.');
      return;
    }
    setBusy(true);
    try {
      const created = await createMatterDeadline(matterId, {
        description: description.trim(),
        dueDate: new Date(`${dueDate}T00:00:00Z`).toISOString(),
        dueDateText: 'added by hand',
      });
      onAdded(created);
      setDescription('');
      setDueDate('');
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the deadline.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-amber-500 hover:text-amber-400"
      >
        <Plus className="h-3.5 w-3.5" /> Add a deadline
      </button>
    );
  }

  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What must be done"
          className="rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none"
        />
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 focus:border-amber-700/60 focus:outline-none"
        />
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void add()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-stone-950 hover:bg-amber-700 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Add
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-300"
        >
          Cancel
        </button>
      </div>
    </Card>
  );
}

function DeadlineRow({
  deadline,
  onStatus,
  onDelete,
}: {
  deadline: Deadline;
  onStatus: (s: DeadlineStatus) => void;
  onDelete: () => void;
}) {
  const urgency = urgencyOf(deadline.dueDate, deadline.status);
  return (
    <Card variant="default" padding="md" className={`flex items-start gap-3 ${urgencyClasses(urgency)}`}>
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${deadline.status === 'DONE' ? 'text-stone-500 line-through' : 'text-stone-200'}`}>
          {deadline.description}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <span className="font-medium">{formatDueDate(deadline.dueDate)}</span>
          <span className="uppercase tracking-[0.15em] opacity-80">
            {relativeDue(deadline.dueDate, deadline.status)}
          </span>
          {deadline.dueDateText ? <span className="text-stone-500">“{deadline.dueDateText}”</span> : null}
          {deadline.sourceDocumentId ? (
            <span className="inline-flex items-center gap-1 text-stone-500">
              <FileText className="h-3 w-3" /> from a court order
            </span>
          ) : null}
        </div>
      </div>
      <select
        value={deadline.status}
        onChange={(e) => onStatus(e.target.value as DeadlineStatus)}
        className="shrink-0 rounded border border-stone-700 bg-stone-900 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-stone-300 focus:outline-none"
        aria-label="Deadline status"
      >
        <option value="UPCOMING">Upcoming</option>
        <option value="DONE">Done</option>
        <option value="MISSED">Missed</option>
      </select>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete deadline"
        title="Delete"
        className="shrink-0 rounded p-1 text-stone-600 transition-colors hover:text-red-400"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </Card>
  );
}
